import {
  ContentState,
  EditorState,
  SelectionState,
  convertFromRaw,
  genKey,
} from 'draft-js';
import { fromJuryBoxToDraftJs } from './Transformer';
import { merge3LineRefs } from '../../collaboration/diff3Lines';

export function buildEditorStateFromContent(
  comments,
  richTextStyles,
  hasEllipsis = false,
) {
  const text = comments || '';

  if (
    richTextStyles &&
    Array.isArray(richTextStyles) &&
    richTextStyles.length > 0
  ) {
    const state = convertFromRaw(
      fromJuryBoxToDraftJs(
        {
          comments: text,
          richTextStyles,
        },
        hasEllipsis,
      ),
    );
    return EditorState.createWithContent(state);
  }

  if (text) {
    return EditorState.createWithText(text);
  }

  return EditorState.createEmpty();
}

/**
 * Fold a remote update into the current editor state without rebuilding
 * the blocks the user is working in.
 *
 * Lines that are unchanged locally keep their existing ContentBlock
 * instances (and therefore their keys), so the user's selection survives
 * remote merges untouched. Only lines that actually came from the remote
 * side are new blocks.
 */
export function reconcileRemoteContent(
  editorState,
  baseText,
  remoteText,
  remoteStyles,
) {
  const currentContent = editorState.getCurrentContent();
  const currentText = currentContent.getPlainText();
  const normalizedRemote = remoteText || '';

  if (currentText === normalizedRemote) {
    return editorState;
  }

  const baseLines = (baseText || '').split('\n');
  const currentLines = currentText.split('\n');
  const remoteLines = normalizedRemote.split('\n');

  const selection = editorState.getSelection();
  const currentBlocks = currentContent.getBlocksAsArray();

  let cursorLine = 0;
  currentBlocks.forEach((block, idx) => {
    if (block.getKey() === selection.getAnchorKey()) {
      cursorLine = idx;
    }
  });

  const mergedRefs = merge3LineRefs(baseLines, currentLines, remoteLines);

  const remoteBlocks = buildEditorStateFromContent(
    normalizedRemote,
    remoteStyles,
  )
    .getCurrentContent()
    .getBlocksAsArray();

  const usedKeys = new Set();
  const mergedBlocks = [];
  for (const ref of mergedRefs) {
    const pool = ref.source === 'current' ? currentBlocks : remoteBlocks;
    let block = pool[ref.index];
    if (!block) {
      continue;
    }
    if (usedKeys.has(block.getKey())) {
      block = block.set('key', genKey());
    }
    usedKeys.add(block.getKey());
    mergedBlocks.push(block);
  }

  const newContent = mergedBlocks.length
    ? ContentState.createFromBlockArray(mergedBlocks)
    : ContentState.createFromText('');

  const next = EditorState.push(editorState, newContent, 'insert-fragment');

  // Restore the selection. Anchor/focus blocks normally survive because
  // locally-owned lines keep their block instances.
  let nextSelection = selection;
  if (
    !newContent.getBlockForKey(selection.getAnchorKey()) ||
    !newContent.getBlockForKey(selection.getFocusKey())
  ) {
    const blocks = newContent.getBlocksAsArray();
    const idx = Math.max(0, Math.min(cursorLine, blocks.length - 1));
    const block = blocks[idx];
    const offset = Math.min(selection.getAnchorOffset(), block.getLength());
    nextSelection = SelectionState.createEmpty(block.getKey()).merge({
      anchorOffset: offset,
      focusOffset: offset,
      hasFocus: selection.getHasFocus(),
    });
  }

  return selection.getHasFocus()
    ? EditorState.forceSelection(next, nextSelection)
    : EditorState.acceptSelection(next, nextSelection);
}
